"use client";

import React, { useCallback, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import PageWithHeaderLayoutProps, {
  HeaderButton,
} from "@/features/layouts/PageWithHeaderLayout";
import VerifyUserForm, {
  VerifyUserFormRef,
} from "@/features/user/VerifyUserForm";
import Icon from "@/components/misc/Icon";
import Button from "@/components/input/Button";
import SplashScreen from "@/features/pages/app/SplashScreen";
import {
  getContractRejectPageUrl,
  useConfirmFundEmailModal,
  useContractDetails,
  useContractUrlId,
} from "@/services/ContractsService";
import { SITE_PATHS } from "@/config/routing";
import { analytics, appFeatures, contractService } from "@/config/services";
import { useAuth } from "@/services/Authentication";
import { useResponsive } from "@/styles";
import Logo from "@/features/app/Logo";
import OfferSeo from "@/features/contracts/OfferSeo";

import {
  StyledPage,
  StyledProposalButtonsContainer,
  StyledProposalControls,
  StyledContractPreview,
  StyledProposalStep,
  StyledSmallText,
} from "./ContractProposalPage.styles";
import LandingScreen from "./components/LandingScreen";

const ContractProposalPage: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const { isScreenSmallerThanTablet } = useResponsive();

  const navigate = useNavigate();
  const { idFromUrl: contractIdFromUrl } = useContractUrlId();
  const [verifyUserFormRef, setVerifyUserFormRef] = useState(
    {} as VerifyUserFormRef
  );
  const [userVerified] = useState(true);

  const { contractDetails, isLoading: isLoadingContractDetails } =
    useContractDetails({
      redirectIfNotFound: true,
      useContractIdFromUrl: true,
      useAuthToken: false,
    });
  const [showForm, setShowForm] = useState(isAuthenticated);
  const [isLoadingPaymentUrl, setIsLoadingPaymentUrl] = useState(false);

  //-------------------

  const handleBackClick = useCallback(() => {
    if (!userVerified) {
      if (verifyUserFormRef) {
        verifyUserFormRef.previousStep();
      }
    } else {
      navigate(SITE_PATHS.CONTRACTS_PAGE);
    }
  }, [userVerified, verifyUserFormRef, navigate]);

  const handleAccept = useCallback(() => {
    setIsLoadingPaymentUrl(true);

    if (isLoadingPaymentUrl) {
      return true;
    }

    if (contractDetails) {
      analytics.triggerAccpetOfferEvent({
        contractId: contractDetails.id,
      });

      contractService
        .acceptContract(contractDetails)
        .then(() => {
          setTimeout(() => {
            setIsLoadingPaymentUrl(false);
          }, 2000);
        })
        .catch(() => {
          setIsLoadingPaymentUrl(false);
        });
    }
  }, [contractDetails, isLoadingPaymentUrl]);
  const { modalJsx: emailConfirmModalJsx, modalState: emailConfirmModalState } =
    useConfirmFundEmailModal({
      ctaClick: handleAccept,
    });

  //-------------------

  if (isLoadingPaymentUrl || isLoadingContractDetails || !contractDetails) {
    return <SplashScreen />;
  }

  const seoJsx = <OfferSeo contractId={contractDetails.id} />;

  if (!showForm) {
    return (
      <>
        {seoJsx}

        <LandingScreen
          contractDetails={contractDetails}
          ctaButtonProps={{
            onClick: () => {
              setShowForm(true);
            },
          }}
        />
      </>
    );
  }

  const rejectUrl = getContractRejectPageUrl(contractIdFromUrl);
  const tAndCText = (
    <StyledSmallText className="mt-2">
      By clicking accept, you are agreeing to the terms of this offer and
      Paypipe's <Link to={SITE_PATHS.TERMS_CONDITIONS_PAGE}>Terms of Use</Link>{" "}
      and <Link to={SITE_PATHS.POLICY_PAGE}>Privacy Policy</Link>
    </StyledSmallText>
  );
  const ctaButtons = (
    <StyledProposalButtonsContainer className="mt-3">
      {appFeatures.isSupported("CONTRACT.REJECT") && (
        <Button variant="secondary" colorVariant="danger" link={rejectUrl}>
          Reject
        </Button>
      )}

      <Button onClick={emailConfirmModalState.open}>Accept and fund</Button>
    </StyledProposalButtonsContainer>
  );

  const ctaContent = isScreenSmallerThanTablet ? (
    <>
      {tAndCText}
      {ctaButtons}
    </>
  ) : (
    <>
      {ctaButtons}
      {tAndCText}
    </>
  );

  return (
    <>
      {emailConfirmModalJsx}

      {seoJsx}

      <PageWithHeaderLayoutProps
        headerProps={{
          titleProps: { children: userVerified ? "View Offer" : "" },
          backButtonProps: isScreenSmallerThanTablet
            ? {
                onClick: handleBackClick,
                children: userVerified ? (
                  <Icon isSrcRelative src="cross.svg" customSize="0.75rem" />
                ) : null,
              }
            : { show: false },
          appendContent: isScreenSmallerThanTablet ? (
            userVerified ? (
              <HeaderButton>
                <Icon isSrcRelative src="menu.svg" size="sm" />
              </HeaderButton>
            ) : null
          ) : (
            <Button variant="ghost" onClick={handleBackClick}>
              <Icon isSrcRelative src="cross.svg" size="sm" />
            </Button>
          ),
          prependContent: isScreenSmallerThanTablet ? null : (
            <Logo redirectToMainSite />
          ),
        }}
        contentContainerProps={{
          style: { background: "var(--clr-background-ukwn-8, #fbfbfb)" },
        }}
      >
        <StyledPage>
          {userVerified ? (
            <StyledProposalStep>
              <StyledContractPreview
                contractDetails={contractDetails}
                ctaContent={ctaContent}
              />

              {isScreenSmallerThanTablet && (
                <StyledProposalControls>{ctaContent}</StyledProposalControls>
              )}
            </StyledProposalStep>
          ) : (
            <VerifyUserForm getFormRef={setVerifyUserFormRef} />
          )}
        </StyledPage>
      </PageWithHeaderLayoutProps>
    </>
  );
};

export default ContractProposalPage;
